import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import LockIcon from '@/assets/icons/lockNew';
import { Button } from '@/components/ButtonComponent';
import { AnimatedPressableOpacity } from '@/components/animated-pressable-opacity/AnimatedPressableOpacity';
import { Box, Row } from '@/components/lib/components';
import { TileProfileImage } from '@/components/player-profile/tiles/TileProfileImage';
import { borderWidth, tilePaddingTop } from '@/components/player-profile/tiles/utils';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { isPlayerSelected } from '@/feature/betslip-pickem/utils/betslip-utils';
import { usePlayerProfileDataStore } from '@/feature/player-profile-sheets/hooks/use-player-profile-data-store';
import { designSystem, withOpacity } from '@/styles/styles';
import { MarketStatus, ProjectionType } from '@/types/api.generated';
import { defaultZustandCompareFunction } from '@/utils/default-zustand-compare-function';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { getPlayerArcDetails } from '@/utils/get-player-arc-details';
import { sortProjections } from '@/utils/sortProjections';

import { PickSelectionMethods } from '../../hooks/use-pick-selection';
import { usePlayerFilters } from '../../hooks/use-player-filters';
import { checkIfDiscountedProjection } from '../../utils/lineupsUtils';
import { POPULAR_PLAYERS_FILTER } from '../../utils/players';
import { PickButtonsWithFunctionality } from '../PickProjectionButtonsSection';
import { PlayerDetailsTile } from '../PlayerDetails';
import { ProjectionDescriptionTile } from './ProjectionDescriptionTile';

type PlayerTileSpecificProps = {
    event?: EventInfo;
    testID?: string;
    analyticsTag?: string;
    player: PlayerWithTeam;
    containerWidth: number;
    playerProjections: PlayerProjectionFragment[];
    hideGameTime?: boolean;
    openPlayerProfile: () => void;
} & Partial<PickSelectionMethods>;

/**
 * Projection card tile used to toggle between different projection types.
 * @param {PlayerTileSpecificProps} props
 * @returns {JSX.Element}
 */
export const PlayerTile = (props: PlayerTileSpecificProps) => {
    const { player, event, testID, analyticsTag, openPlayerProfile, containerWidth, playerProjections, hideGameTime } =
        props;

    const team = player.team;
    const league = event?.league || team?.league || player.league;
    const { leagueColor, leagueIcon } = useLeagueConfigsStore(
        leagueConfigSelector(league),
        defaultZustandCompareFunction
    );

    const { teamLogo, arcText } = getPlayerArcDetails(player, league, team);
    const playerNumber = getPlayerJerseyNumber(league, player?.jerseyNumber);

    const { playerProjectionFilter } = usePlayerFilters();
    const playerPickedEntry = isPlayerSelected({ eventId: event?.id, playerId: player && player.id });
    const setPopularPlayerProjection = usePlayerProfileDataStore(state => state.actions.setPopularPlayerProjection);

    const projectionFilter = useMemo(() => {
        if (playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            const nonSuspended = playerProjections
                .filter(it => it.marketStatus !== MarketStatus.Suspended)
                .sort(sortProjections);
            if (nonSuspended.length === 0) {
                //If all projections are suspended, just fetch the first one
                return playerProjections.sort(sortProjections)?.[0]?.key;
            }

            //If filtering popular first look for a discounted projection
            const special = nonSuspended.find(it => checkIfDiscountedProjection(it.type));
            if (special) {
                return special.key;
            }
            //Otherwise, randomly pick a random projection, from the top 6 projections
            //This ensures that not all players on the 'Popular' tab will have the same projection selected
            const randomIdx = hashIds(event?.id ?? '', player.id, Math.min(nonSuspended.length, 6));
            return nonSuspended[randomIdx]?.key;
        } else {
            return playerProjectionFilter;
        }
    }, [event?.id, player.id, playerProjectionFilter, playerProjections]);

    //All the projections for this player, for the selected projection filter
    const filteredProjections = useMemo(() => {
        const projectionsByFilter = playerProjections.filter(it => it.key === projectionFilter);
        const allProjectionsByFilterSuspended = projectionsByFilter.every(
            it => it.marketStatus === MarketStatus.Suspended
        );
        // Conditionally filter out suspended markets if not all filtered markets are suspended
        // If all are suspended, we want to show the suspended state for the entire tile
        let computedProjections = allProjectionsByFilterSuspended
            ? projectionsByFilter
            : projectionsByFilter.filter(it => it.marketStatus === MarketStatus.Opened);

        return computedProjections.sort(sortProjections);
    }, [projectionFilter, playerProjections]);

    // Group projections by value, for example:
    // value 20 - [proj1]
    // value 25 - [proj2, proj3]
    // value 30 - [proj4]
    const projectionsByValue: { value: number; projections: PlayerProjectionFragment[] }[] = useMemo(() => {
        return filteredProjections
            .reduce((acc: { value: number; projections: PlayerProjectionFragment[] }[], projection) => {
                const value = projection.nonRegularValue || projection.value;
                const existing = acc.find(it => it.value === value);
                if (projection.marketStatus === MarketStatus.Suspended) {
                    //Skip suspended projections
                    return acc;
                }
                if (existing) {
                    existing.projections.push(projection);
                } else {
                    acc.push({ value, projections: [projection] });
                }
                return acc;
            }, [])
            .sort((a, b) => a.value - b.value);
    }, [filteredProjections]);

    //Index used to toggle between different markets/values for the same projection type
    const [projectionIndex, setProjectionIndex] = useState<number>(0);

    const [projectionsByValueIndex, setProjectionsByValueIndex] = useState<number>(() => {
        //First check if the player has a picked projection select the index of that projection
        //If the player has discounted projection, we will select that one
        //or the first regular projection.
        //Otherwise if no regular or special projection default to the first projection value
        if (playerPickedEntry) {
            const pickedIdx = projectionsByValue.findIndex(group =>
                group.projections.some(
                    p => p.key === playerPickedEntry.projection.key && p.type === playerPickedEntry.projection.type
                )
            );
            if (pickedIdx >= 0) {
                return pickedIdx;
            }
        }
        const specialProjIdx = projectionsByValue.findIndex(it =>
            it.projections.find(p => checkIfDiscountedProjection(p.type))
        );
        if (specialProjIdx >= 0) {
            return specialProjIdx;
        }
        const regularProjIdx = projectionsByValue.findIndex(it =>
            it.projections.find(p => p.type === ProjectionType.Regular)
        );
        if (regularProjIdx >= 0) {
            return regularProjIdx;
        }
        return 0;
    });

    const handleToggleProjection = useCallback(
        (direction: 'prev' | 'next') => {
            if (direction === 'next') {
                setProjectionsByValueIndex(index => index + 1);
            } else {
                setProjectionsByValueIndex(index => index - 1);
            }
            setProjectionIndex(0);
        },
        [setProjectionIndex]
    );

    const selectedProjectionsByValue = projectionsByValue[projectionsByValueIndex];
    // If there are no projections for the current value (if all suspended - as those are filtered),
    // we fallback to the first one, as this will allow us to display the suspended state for the tile
    // This can still be undefined in rare cases (when selected projection is removed by a WS update in LIVE games),
    // in which case we are supposed to show the suspended tile
    const projection = selectedProjectionsByValue?.projections[projectionIndex] ?? filteredProjections[0];

    const hasNextProjectionsByValue = projectionsByValueIndex < projectionsByValue.length - 1;
    const hasPreviousProjectionsByValue = projectionsByValueIndex > 0;

    const isThisProjectionPicked =
        projection &&
        playerPickedEntry?.projection.key === projection.key &&
        playerPickedEntry?.projection.type === projection.type;
    const allFilteredProjectionsSuspended =
        filteredProjections.length > 0 && filteredProjections.every(p => p.marketStatus === MarketStatus.Suspended);

    const openPlayerCard = () => {
        openPlayerProfile();
        if (playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            setPopularPlayerProjection(projectionFilter);
        }

        if (analyticsTag) {
            BetrAnalytics.trackEvent(AnalyticsEvent.EXPAND_PLAYER_PROPS, {
                location: analyticsTag,
            });
        }
    };
    const tileProps = isThisProjectionPicked
        ? { mode: 'selection' as const, projection, outcome: playerPickedEntry?.outcome }
        : { mode: 'highlight' as const, projection };

    const handleSwapProjection = () => {
        const numberOfProjections = selectedProjectionsByValue?.projections.length || 0;
        setProjectionIndex(prevIndex => (prevIndex + 1) % numberOfProjections);
    };

    return (
        <AnimatedPressableOpacity onPress={openPlayerCard} accessible={false} flex={1}>
            <Box
                style={[
                    styles.container,
                    {
                        paddingTop: tilePaddingTop,
                    },
                ]}
            >
                <TileProfileImage
                    key={`${player.firstName}-${player.lastName}-${player.id}`}
                    playerImageUrl={player.icon}
                    teamImageUrl={teamLogo ?? leagueIcon}
                    teamColor={team?.color ?? leagueColor}
                    bgColor={'gray6'}
                    playerNumber={playerNumber}
                    containerWidth={containerWidth}
                    selectedProjection={projection}
                    event={event}
                    player={player}
                    teamName={team?.name}
                    secondaryColor={team?.secondaryColor}
                    arcText={arcText}
                />
                <Box style={styles.playerDetailsContainer}>
                    <PlayerDetailsTile
                        isSuspended={allFilteredProjectionsSuspended}
                        projections={playerProjections}
                        player={player}
                        event={event}
                        isVoided={false}
                        teamName={team?.name || ''}
                        selectedProjection={projection}
                        testID={`playerEventDetails-${testID}`}
                        hideGameTime={hideGameTime}
                    />
                </Box>
                <Box flex={1} />
                <Row style={styles.projectionDescriptionContainer}>
                    {projection ? (
                        <ProjectionDescriptionTile
                            selectedProjection={projection}
                            isSuspended={allFilteredProjectionsSuspended}
                            handleToggleProjection={handleToggleProjection}
                            hasNextProjection={hasNextProjectionsByValue}
                            hasPreviousProjection={hasPreviousProjectionsByValue}
                        />
                    ) : null}
                </Row>
                <Box>
                    {!allFilteredProjectionsSuspended && projection ? (
                        <PickButtonsWithFunctionality
                            testID={testID}
                            {...props}
                            leagueIcon={leagueIcon}
                            leagueColor={leagueColor}
                            selectedProjection={projection}
                            selectedProjectionsByValue={selectedProjectionsByValue}
                            swapProjection={handleSwapProjection}
                            viewMode={'tile'}
                            containerWidth={containerWidth}
                            {...tileProps}
                        />
                    ) : (
                        <Button disabled label={<LockIcon />} style={styles.suspendedButton} />
                    )}
                </Box>
            </Box>
        </AnimatedPressableOpacity>
    );
};

/**
 * Hash function that generates a number between 0-max from two string IDs
 * @param id1 First ID string (format like "682bfe517fd44f648eebff1c")
 * @param id2 Second ID string (format like "682bfe517fd44f648eebff1c")
 * @returns number from 0-max
 */
function hashIds(id1: string, id2: string, max: number): number {
    // Combine both IDs
    const combined = id1 + id2;

    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
        // hash * 33 + char code (ensures good distribution for strings)
        // eslint-disable-next-line no-bitwise
        hash = (hash << 5) + hash + combined.charCodeAt(i);
    }
    // Map to range 0-max
    const result = Math.abs(hash) % max;
    return result;
}

const styles = StyleSheet.create({
    playerDetailsContainer: {
        marginTop: 57,
        marginBottom: 8,
        zIndex: 1,
    },
    projectionDescriptionContainer: {
        marginBottom: 14,
    },
    container: {
        borderColor: withOpacity(designSystem.colors.white, '0.02'),
        borderRadius: 16,
        paddingBottom: 16,
        paddingHorizontal: 16,
        borderWidth: borderWidth,
        backgroundColor: designSystem.colors.neutral800,
        borderCurve: 'continuous',
        flexGrow: 1,
        overflow: 'hidden',
    },

    content: {
        marginTop: 49,
        marginBottom: 8,
    },
    suspendedButton: {
        padding: 10,
        borderRadius: 10,
        backgroundColor: designSystem.colors.gray6,
        justifyContent: 'center',
        alignItems: 'center',
        borderCurve: 'continuous',
        borderWidth: 0,
    },
});
